/**
 * Microsoft Graph API Client
 *
 * Transport-layer abstraction for all Microsoft Graph API calls.
 * Every fetch() to graph.microsoft.com should go through this client.
 *
 * Use `createGraphClient` to get the appropriate implementation:
 * - Production: MicrosoftGraphClient (real HTTP calls)
 * - Debug mode: DebugMicrosoftGraphClient (configurable error simulation)
 *
 * To enable the debug client, set VITE_DEBUG_GRAPH=true in your environment.
 * Then use the browser console to simulate errors:
 *
 *   __graphDebug.simulateError('/sites/root', 403)
 *   __graphDebug.simulateError('/drives/', 429, { headers: { 'Retry-After': '5' } })
 *   __graphDebug.simulateError('/drives/', 503)
 *   __graphDebug.clearAllErrors()
 *   __graphDebug.listOverrides()
 */

export interface MicrosoftGraphApiClient {
  /** Fetch from graph.microsoft.com/v1.0. `path` should start with `/`. */
  fetch(path: string, init?: RequestInit): Promise<Response>;
}

const GRAPH_BASE_URL = 'https://graph.microsoft.com/v1.0';

/**
 * Real implementation — adds the Authorization header and base URL.
 */
export class MicrosoftGraphClient implements MicrosoftGraphApiClient {
  constructor(private accessToken: string) {}

  async fetch(path: string, init?: RequestInit): Promise<Response> {
    const headers = new Headers(init?.headers);
    headers.set('Authorization', `Bearer ${this.accessToken}`);

    return fetch(`${GRAPH_BASE_URL}${path}`, {
      ...init,
      headers,
    });
  }
}

// ---------------------------------------------------------------------------
// Debug implementation
// ---------------------------------------------------------------------------

interface ErrorOverride {
  status: number;
  body?: Record<string, unknown>;
  headers?: Record<string, string>;
}

/** Module-level overrides so they persist across client instances. */
const debugOverrides = new Map<string, ErrorOverride>();

const debugControls = {
  simulateError(
    pattern: string,
    status: number,
    options?: {
      body?: Record<string, unknown>;
      headers?: Record<string, string>;
    },
  ) {
    debugOverrides.set(pattern, {
      status,
      body: options?.body,
      headers: options?.headers,
    });
    console.log(
      `[GraphDebug] Will return ${status} for paths matching "${pattern}"`,
    );
  },

  clearError(pattern: string) {
    debugOverrides.delete(pattern);
    console.log(`[GraphDebug] Cleared override for "${pattern}"`);
  },

  clearAllErrors() {
    debugOverrides.clear();
    console.log('[GraphDebug] All overrides cleared');
  },

  listOverrides() {
    return Object.fromEntries(debugOverrides);
  },
};

/**
 * Debug implementation — intercepts requests whose path matches a configured
 * pattern and returns a synthetic error Response. Unmatched requests fall
 * through to the real MicrosoftGraphClient.
 */
export class DebugMicrosoftGraphClient implements MicrosoftGraphApiClient {
  private fallback: MicrosoftGraphClient;

  constructor(accessToken: string) {
    this.fallback = new MicrosoftGraphClient(accessToken);

    if (typeof window !== 'undefined') {
      (window as any).__graphDebug = debugControls;
    }
  }

  async fetch(path: string, init?: RequestInit): Promise<Response> {
    for (const [pattern, override] of debugOverrides) {
      if (path.includes(pattern)) {
        console.warn(
          `[GraphDebug] Intercepted ${init?.method ?? 'GET'} ${path} → ${override.status}`,
        );

        const body = override.body ?? {
          error: {
            code: `debug_${override.status}`,
            message: `Simulated ${override.status} from DebugMicrosoftGraphClient`,
          },
        };

        return new Response(JSON.stringify(body), {
          status: override.status,
          statusText: 'Debug Override',
          headers: {
            'Content-Type': 'application/json',
            ...override.headers,
          },
        });
      }
    }

    return this.fallback.fetch(path, init);
  }
}

// ---------------------------------------------------------------------------
// Factory
// ---------------------------------------------------------------------------

/**
 * Creates the appropriate Graph API client based on the environment.
 *
 * When `VITE_DEBUG_GRAPH=true` in development, returns a DebugMicrosoftGraphClient
 * and exposes `window.__graphDebug` for controlling error simulation from the
 * browser console.
 */
export const createGraphClient = (
  accessToken: string,
): MicrosoftGraphApiClient => {
  if (
    process.env.NODE_ENV === 'development' &&
    import.meta.env.VITE_DEBUG_GRAPH === 'true'
  ) {
    return new DebugMicrosoftGraphClient(accessToken);
  }
  return new MicrosoftGraphClient(accessToken);
};
